import { ThemeProvider, CssBaseline } from '@mui/material';
import { Routes, Route, Navigate } from 'react-router-dom';

import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import { useAuth } from './contexts/AuthContext';
import { WorkspaceProvider, useWorkspace } from './contexts/WorkspaceContext';

// Pages
import AddItem from './pages/AddItem';
import EditItem from './pages/EditItem';
import Landing from './pages/Landing';
import Login from './pages/Login';
import MemberInvite from './pages/MemberInvite';
import Members from './pages/Members';
import Settings from './pages/Settings';
import Sharing from './pages/Sharing';
import Signup from './pages/Signup';
import Unlock from './pages/Unlock';
import Vault from './pages/Vault';
import VaultItemDetail from './pages/VaultItemDetail';
import WorkspaceSetup from './pages/WorkspaceSetup';
import WorkspaceInvite from './pages/WorkspaceInvite';
import { theme } from './theme';
import Devices from './pages/Devices';

function AppRoutes() {
  const { isAuthenticated, isLoading, needsUnlock } = useAuth();
  const { needsWorkspaceSetup, isLoading: workspaceLoading } = useWorkspace();

  if (isLoading || (isAuthenticated && workspaceLoading)) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        Loading...
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/"
        element={isAuthenticated ? <Navigate to="/vault" replace /> : <Landing />}
      />
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/vault" replace /> : <Login />}
      />
      <Route
        path="/signup"
        element={isAuthenticated ? <Navigate to="/vault" replace /> : <Signup />}
      />
      <Route
        path="/unlock"
        element={
          !isAuthenticated ? (
            <Navigate to="/login" replace />
          ) : needsUnlock ? (
            <Unlock />
          ) : (
            <Navigate to="/vault" replace />
          )
        }
      />
      <Route path="/member-invite/:token" element={<MemberInvite />} />
      <Route path="/workspace-invite/:token" element={<WorkspaceInvite />} />

      {/* Workspace setup - required after first signup */}
      <Route
        path="/workspace-setup"
        element={
          !isAuthenticated ? (
            <Navigate to="/login" replace />
          ) : needsUnlock ? (
            <Navigate to="/unlock" replace />
          ) : !needsWorkspaceSetup ? (
            <Navigate to="/vault" replace />
          ) : (
            <WorkspaceSetup />
          )
        }
      />

      {/* Protected routes */}
      <Route element={<ProtectedRoute />}>
        {/* Redirect to workspace setup if needed */}
        <Route
          element={
            needsWorkspaceSetup ? <Navigate to="/workspace-setup" replace /> : <Layout />
          }
        >
          <Route path="/vault" element={<Vault />} />
          <Route path="/vault/:id" element={<VaultItemDetail />} />
          <Route path="/add" element={<AddItem />} />
          <Route path="/edit/:id" element={<EditItem />} />
          <Route path="/sharing" element={<Sharing />} />
          <Route path="/members" element={<Members />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/devices" element={<Devices />} />
        </Route>
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <WorkspaceProvider>
        <AppRoutes />
      </WorkspaceProvider>
    </ThemeProvider>
  );
}

